import React, { useEffect, useState } from 'react'
import { Button, Grid, TextField, Typography } from '@mui/material'
import { WorkOrderDetailsTable } from './WorkOrderDetailsTable'
import { useApi } from '../../hooks/useApi'
import {
  PreInboundCreateDto,
  PreinboundInfoDto,
  PreInboundItemsCreateDto,
  PreInboundReasonsGrid,
  WorkOrderItems
} from './types'
import { createPreInbound, getOrderDevolutionFromWorkOrder } from './api'
import { State } from '../_EOS/State'
import { useStyles } from './styles'
import { ApiResponse } from '../../typings/ApiResponse'
import { useCourier } from '../../hooks/useCourier'
import { GenericDLL } from '../Almacen/UI/GenericDLL'
import { Courier } from '../../interfaces/Courier'
import { LoadingButton } from '../_EOS/Button/LoadingButton'

interface ReturnItemsSelectionProps {
  workOrderId: number
  orderId?: number
  setStep: (step: number) => void
  items: WorkOrderItems[] | undefined
  setItems: React.Dispatch<React.SetStateAction<WorkOrderItems[] | undefined>>
  setIdle: () => void
  handleClearSelectedOT: () => void
  selectedTypeAsnIncome: number
  setPreinBoundId: React.Dispatch<React.SetStateAction<number>>
  setSelectedTrackingNumber: React.Dispatch<React.SetStateAction<string>>
  setSelectedCourier: React.Dispatch<React.SetStateAction<string>>
  handleSnackbar: (msg: string | undefined, status: boolean) => void
}

export const ReturnItemsSelection: React.FC<ReturnItemsSelectionProps> = ({
  workOrderId,
  orderId,
  setStep,
  items,
  setItems,
  setIdle,
  handleClearSelectedOT,
  selectedTypeAsnIncome,
  setPreinBoundId,
  setSelectedTrackingNumber,
  setSelectedCourier,
  handleSnackbar
}) => {
  const {
    getCouriers,
    couriersState: { couriers }
  } = useCourier()
  const classes = useStyles()
  const { state, callApi } = useApi<ApiResponse<WorkOrderItems[]>>()
  const [trackingNumber, setTrackingNumber] = useState<string>('')
  const [courier, setCourier] = useState<number>(-1)
  const [listReasons, setListReasons] = useState<PreInboundReasonsGrid[]>([])
  const { callApi: createCallApi } = useApi()
  const [loading, setLoading] = useState(false)
  const [courierError, setCourierError] = useState(false)
  const [trackingError, setTrackingError] = useState(false)

  // consultamos el registro, puede devolver 1 o +1
  const getOrderDetailsFromWorkOrder = () => {
    callApi(getOrderDevolutionFromWorkOrder(workOrderId, orderId)).then(
      (response) => {
        setItems(response.items)
      }
    )
  }

  useEffect(() => {
    if (workOrderId !== undefined) {
      getOrderDetailsFromWorkOrder()
    }
  }, [workOrderId])

  const setNewReturnedQuantity = (
    quantity: number,
    workOrderDetail: WorkOrderItems
  ) => {
    if (items !== undefined) {
      // Clone the data array and update the changed value

      const elementToChange = items.filter(
        (el) => el.workOrderDetailId === workOrderDetail.workOrderDetailId
      )[0]

      const listWithoutChangedElement = items.filter(
        (el) => el.workOrderDetailId !== workOrderDetail.workOrderDetailId
      )

      const changedElement: WorkOrderItems = {
        ...elementToChange,
        expectedToReturnQuantity: quantity
      }

      // Update the data array
      setItems(
        [...listWithoutChangedElement, changedElement].sort(
          (a, b) => a.workOrderDetailId - b.workOrderDetailId
        )
      )
    }
  }

  const onCheckChange = (workOrderId: number, checked: boolean) => {
    if (items !== undefined) {
      const elementToChange = items.filter(
        (el) => el.workOrderDetailId === workOrderId
      )[0]

      const changedElement: WorkOrderItems = {
        ...elementToChange,
        checked
      }

      const listWithoutChangedElement = items.filter(
        (el) => el.workOrderDetailId !== workOrderId
      )

      setItems(
        [...listWithoutChangedElement, changedElement].sort(
          (a, b) => a.workOrderDetailId - b.workOrderDetailId
        )
      )
    }
  }

  const disableButtonNext = () => {
    if (!items || items.length === 0) return true

    // Si no hay al menos una fila seleccionada => botón deshabilitado
    const anyChecked = items.some((el) => el.checked)
    if (!anyChecked) return true

    // Validación de errores en cantidades de los seleccionados
    const hasErrors = items.some(
      (el) =>
        el.checked && (el.expectedToReturnQuantity === undefined || isError(el))
    )
    if (hasErrors) return true

    return false // Se habilita el botón
  }

  const handleCancelButton = () => {
    handleClearSelectedOT()
    setIdle()
    setItems([])
    setStep(1)
  }

  const isError = (item: WorkOrderItems): boolean => {
    return (
      item.expectedToReturnQuantity >
        item.dispatchedQuantity - item.returnedQuantity ||
      item.expectedToReturnQuantity <= 0
    )
  }

  const handleInputChangeCourier = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCourier(Number(event.target.value))
  }

  const requestCreatePreInbound = () => {
    // Resetear errores previos
    setCourierError(false)
    setTrackingError(false)

    let hasError = false

    if (courier === -1) {
      setCourierError(true)
      hasError = true
    }

    if (trackingNumber.trim() === '') {
      setTrackingError(true)
      hasError = true
    }

    if (hasError) {
      handleSnackbar('Debe completar Courier y Tracking Number', false)
      return // detener Guardar
    }

    // Continúa el flujo NORMAL...
    setLoading(true)

    const checkedItems = items?.filter((item) => item.checked) || []

    for (let i = 0; i < checkedItems.length; i++) {
      const item = checkedItems[i]
      const reason = listReasons.find(
        (r) => r.sku === item.sku && r.orderDetailId === item.orderDetailId
      )

      if (!reason || !reason.preInboundReasonId) {
        setLoading(false)
        handleSnackbar(`Falta motivo para SKU: ${item.sku}`, false)
        return
      }
    }

    const courierName = getCourierNameById(courier)

    const preInboundData: PreInboundCreateDto = {
      referenceId: items?.[0]?.externalOrderId || '',
      orderOriginId: items?.[0]?.orderOriginId || 0,
      trackingNumber,
      courier: courierName,
      typeAsnIncomeId: selectedTypeAsnIncome,
      items: checkedItems.flatMap((item): PreInboundItemsCreateDto[] => {
        const reasonsForItem = listReasons.filter(
          (r) => r.sku === item.sku && r.orderDetailId === item.orderDetailId
        )

        // Si no hay motivos => error
        if (reasonsForItem.length === 0) {
          return [
            {
              orderDetailId: item.orderDetailId || 0,
              sku: item.sku,
              quantity: item.expectedToReturnQuantity || 0,
              reasonId: 0
            }
          ]
        }

        // Generar un registro por cada reasonId
        return reasonsForItem.map((reason) => ({
          orderDetailId: item.orderDetailId || 0,
          sku: item.sku,
          quantity: reason.quantity || 0,
          reasonId: reason.preInboundReasonId || 0
        }))
      })
    }

    createCallApi(createPreInbound(preInboundData))
      .then((data: PreinboundInfoDto) => {
        setPreinBoundId(data.preInboundId)
        setStep(4)
        setLoading(false)
        setSelectedTrackingNumber(trackingNumber)
        setSelectedCourier(courierName)
      })
      .catch((e) => {
        handleSnackbar(`error: ${e.error}`, false)
        console.error(`error:`, e.error)
      })
  }

  const getCourierNameById = (courierId: number): string => {
    const courier = couriers?.find(
      (courier: Courier) => courier.courierId === courierId
    )
    return courier ? courier.name : ''
  }

  useEffect(() => {
    if (couriers?.length === 0) {
      getCouriers()
    }
  }, [])

  return (
    <State state={state}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={5}>
              <div>
                <Typography variant='body2'>Courier:</Typography>
                <GenericDLL
                  fullWidth
                  initialValues={couriers || []}
                  onChange={handleInputChangeCourier}
                  title='Courier'
                  name='name'
                  valueKey={'courierId'}
                  value={courier}
                  error={courierError}
                  helperText={courierError ? 'Debe seleccionar un courier' : ''}
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <div>
                <Typography variant='body2'>Tracking Number:</Typography>
                <TextField
                  name='Tracking Number'
                  value={trackingNumber}
                  onChange={(e) => {
                    setTrackingNumber(e.target.value)
                    if (trackingError) setTrackingError(false) // limpiar error en cambio
                  }}
                  error={trackingError}
                  helperText={
                    trackingError ? 'Debe ingresar Tracking Number' : ''
                  }
                  variant='outlined'
                  fullWidth
                  inputProps={{ maxLength: 100 }}
                />
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <WorkOrderDetailsTable
            data={items || []}
            handleValueChange={setNewReturnedQuantity}
            handleCheckChange={onCheckChange}
            listReasons={listReasons}
            setListReasons={setListReasons}
          />
        </Grid>
        <Grid item xs={6}>
          <div className={classes.centerButton}>
            <Button
              onClick={() => handleCancelButton()}
              variant='outlined'
              color='primary'
            >
              Cancelar
            </Button>
          </div>
        </Grid>
        <Grid item xs={6}>
          <div className={classes.centerButton}>
            {/* Crear Pre Asn */}
            <LoadingButton
              loading={loading}
              variant='contained'
              color='primary'
              onClick={requestCreatePreInbound}
              disabled={disableButtonNext()}
            >
              Guardar
            </LoadingButton>
          </div>
        </Grid>
      </Grid>
    </State>
  )
}
